//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';

import {
  isInstagramFeedAutomaticPostConfig,
  isInstagramReelsAutomaticPostConfig,
  isInstagramStoryGroupAutomaticPostConfig,
  isTikTokVideoAutomaticPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  IFacebookPostAttemptFailure,
  ITikTokPostAttemptFailure,
  ITwitterPosterException,
} from '@apis/universal-posts/scheduled-posts/postAttempts/universalScheduledPostAttemptsModel';
import { IScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  AllUniversalPostTypes,
  UniversalPostType,
  allUniversalPostingGroupNames,
  facebookPostingGroupNames,
  instagramPostingGroupNames,
  pinterestPostingGroupNames,
  tiktokPostingGroupNames,
  twitterPostingGroupNames,
  youtubePostingGroupNames,
} from '@apis/universal-posts/universalPostsApiModel';
import {
  getStandardPostDetails,
  isDraftPost,
  isScheduledPost,
} from '@apis/universal-posts/universalPostsUtils';
import { IPostConnectionInfo } from '@cross-posting/CrossPostDeepLink';
import { FACEBOOK_MAX_IMAGE_FILE_SIZE_MB } from '@facebook/constants/facebookConstants';
import { getGenericPostTimeErrorMessage } from '@instagram-account/post-details/postDetailsUIConstants';
import DateString from '@models/DateString';
import DraftId from '@models/DraftId';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import MediaType from '@models/MediaType';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { SocialNetwork } from '@models/SocialNetwork';
import TimeZoneId from '@models/TimeZoneId';
import { UniversalPostableCustomStatus } from '@models/UniversalPostableCustomStatus';
import { hasPermissionForTask } from '@services/permissions';
import { EditablePostStatuses } from '@universal-scheduling/components/calendar/UniversalCalendarConstants';
import {
  getMonthViewEndDate,
  getMonthViewStartDate,
  getStartOfCurrentMonth,
} from '@universal-scheduling/components/calendar/month/CalendarMonthViewUtils';
import { getWeekViewStartDate } from '@universal-scheduling/components/calendar/week/CalendarWeekViewUtils';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { isDefined } from '@utils/filterPredicates';
import { momentFromDate } from '@utils/moment';

import { StateParams } from 'app/initialization/routing/stateParamModels';
import { RoutableStateName } from 'app/top-level-states';

/**
 * Gets the endDate 6 days after the startDate
 */
export const getTimelineViewEndDate = (startDate: DateString): DateString => {
  return DateString.addDays(startDate, 6);
};

// TODO PD-257849: I think we can delete this or edit it to use elsewehere
export const getDefaultPostTimeMs = (
  date: DateString,
  timeZone: TimeZoneId,
): number => {
  return DateString.toMoment(date, timeZone).hour(12).valueOf();
};

export const getIsPastDue = (post: IScheduledPost | IDraftPost, date: Date) => {
  const { isManual, scheduledTime } = getStandardPostDetails(post);
  const isPastScheduledTime = scheduledTime ? scheduledTime < date : false;

  return (
    isManual &&
    isScheduledPost(post) &&
    isPastScheduledTime &&
    post.customStatus === UniversalPostableCustomStatus.NoAction &&
    post.status !== PostableStatus.Posted
  );
};

export const isEditable = (post: IScheduledPost | IDraftPost) =>
  getIsPastDue(post, new Date()) ||
  isDraftPost(post) ||
  (EditablePostStatuses.includes(post.status) &&
    post.customStatus !== UniversalPostableCustomStatus.PostHidden);

export const getIsProcessingCompletePosted = (
  post: IScheduledPost | IDraftPost,
) =>
  isInstagramStoryGroupAutomaticPostConfig(post.config) ||
  isInstagramFeedAutomaticPostConfig(post.config) ||
  isInstagramReelsAutomaticPostConfig(post.config) ||
  isTikTokVideoAutomaticPostConfig(post.config);

export const getPostingGroupsForNetwork = (network: SocialNetwork | 'All') => {
  switch (network) {
    case SocialNetwork.Facebook:
      return facebookPostingGroupNames;
    case SocialNetwork.Instagram:
      return instagramPostingGroupNames;
    case SocialNetwork.Pinterest:
      return pinterestPostingGroupNames;
    case SocialNetwork.TikTok:
      return tiktokPostingGroupNames;
    case SocialNetwork.Twitter:
      return twitterPostingGroupNames;
    case SocialNetwork.YouTube:
      return youtubePostingGroupNames;
    default:
      return allUniversalPostingGroupNames;
  }
};

export const hasPermissionForNetwork = (network?: SocialNetwork) => {
  const permission = isDefined(network)
    ? getPermissionForNetwork(network)
    : undefined;
  return isDefined(permission) ? hasPermissionForTask(permission) : false;
};

export const getPermissionsForNetworks = (networks: SocialNetwork[]) => {
  return networks.map(getPermissionForNetwork).filter(isDefined);
};

export const getPermissionForNetwork = (
  network: SocialNetwork | undefined,
): string | undefined => {
  switch (network) {
    case SocialNetwork.Facebook:
      return 'publish:schedule_instagram_item';
    case SocialNetwork.Instagram:
      return 'publish:schedule_instagram_item';
    case SocialNetwork.Pinterest:
      return 'publish:schedule_pinterest_item';
    case SocialNetwork.TikTok:
      return 'publish:schedule_instagram_item';
    case SocialNetwork.Twitter:
      return 'publish:schedule_instagram_item';
    case SocialNetwork.YouTube:
      return 'publish:schedule_instagram_item';
    default:
      return undefined;
  }
};

export const getEditPostRouteParams = (
  post: IScheduledPost | IDraftPost,
): { draftPostId?: DraftId; scheduledPostId?: PostableId } => {
  return isDraftPost(post)
    ? { draftPostId: post.id }
    : { scheduledPostId: post.id };
};

export const getConnectionEditPostRouteParams = (
  connectionInfo: IPostConnectionInfo,
): StateParams<RoutableStateName> => {
  const { draftId, postType, postableId } = connectionInfo;

  if (isDefined(postableId)) {
    return { scheduledPostId: postableId };
  } else if (isDefined(draftId)) {
    return { draftPostId: draftId };
  } else {
    return { postType: [postType] };
  }
};

export const getNetworkEditPostRouteParams = (
  post: IScheduledPost | IDraftPost,
): StateParams<RoutableStateName> => {
  const {
    config: { socialNetwork },
  } = post;

  return getNetworkEditPostIdRouteParams(
    socialNetwork,
    isDraftPost(post) ? undefined : post.id,
    isDraftPost(post) ? post.id : undefined,
  );
};

export const getNetworkEditPostIdRouteParams = (
  socialNetwork: SocialNetwork | undefined,
  postableId?: PostableId,
  draftId?: DraftId,
): StateParams<RoutableStateName> => {
  switch (socialNetwork) {
    case SocialNetwork.Facebook:
      return {
        targetDraftId: draftId,
        targetScheduledPostId: postableId,
      };
    case SocialNetwork.Instagram:
      return {
        targetDraftPostId: draftId,
        targetScheduledPostId: postableId,
      };
    case SocialNetwork.Pinterest:
    case SocialNetwork.TikTok:
    case SocialNetwork.Twitter:
    case SocialNetwork.YouTube:
      return {
        draftPostId: draftId,
        scheduledPostId: postableId,
      };
    default:
      return {};
  }
};

export const getAvailablePostTypesForCopying = (
  postMedia: IMlsMediaItem[],
): UniversalPostType[] => {
  if (
    postMedia.length === 1 &&
    postMedia[0]?.networkItem?.mediaType === MediaType.Video
  ) {
    return AllUniversalPostTypes;
  }

  if (postMedia.length === 0) {
    return AllUniversalPostTypes.filter(
      (type) => type !== UniversalPostType.InstagramReelsAutomaticPost,
    );
  }

  const availablePostTypes = [];
  const areAllImages = postMedia.every(
    (media) => media.networkItem?.mediaType === MediaType.Image,
  );

  if (postMedia.length <= 10) {
    availablePostTypes.push(
      UniversalPostType.InstagramFeedAutomaticPost,
      UniversalPostType.InstagramStoryGroupPost,
    );
  }

  if (areAllImages) {
    availablePostTypes.push(UniversalPostType.FacebookPageFeedPost);
  }

  if (postMedia.length <= 5 && areAllImages) {
    availablePostTypes.push(UniversalPostType.PinterestOrganicPin);
  }

  if (postMedia.length <= 4 && areAllImages) {
    availablePostTypes.push(UniversalPostType.TwitterMediaPost);
  }

  return availablePostTypes;
};

export const getViewDates = (
  date: DateString,
  startOfWeek: number,
  view: UniversalCalendarView,
  adjustment: -1 | 0 | 1 = 0,
) => {
  switch (view) {
    case UniversalCalendarView.Month: {
      const adjustedDate =
        adjustment === 0
          ? date
          : DateString.applyMomentOperation(
              getStartOfCurrentMonth(date),
              'add',
              adjustment,
              'M',
            );
      const startDate = getMonthViewStartDate(adjustedDate, startOfWeek);
      const endDate = getMonthViewEndDate(startDate, startOfWeek);
      return { startDate, endDate };
    }
    case UniversalCalendarView.Week:
    case UniversalCalendarView.Timeline: {
      const adjustedDate = DateString.applyMomentOperation(
        date,
        'add',
        adjustment,
        'w',
      );
      const startDate = getWeekViewStartDate(adjustedDate, startOfWeek);
      const endDate = DateString.addDays(startDate, 6);
      return { startDate, endDate };
    }
  }
};

export const getHasPostedTimeError = (post: IScheduledPost | IDraftPost) => {
  const hasDateError = momentFromDate(
    post.scheduledTime ?? new Date(),
    TimeZoneId.Utc,
  ).isBefore(momentFromDate(new Date(), TimeZoneId.Utc));

  return hasDateError;
};

export const getOrderedMediaItems = (post: IScheduledPost | IDraftPost) => {
  const cuids = post.config.cuids;

  return cuids
    .map((cuid) => PojoMap.get(post.mediaItems, cuid))
    .filter(isDefined);
};

export const getNavigationRouteOnDraftSave = ({
  defaultRoute,
  entryPoint,
  postTime,
}: {
  defaultRoute: RoutableStateName;
  entryPoint?: RoutableStateName;
  postTime?: Date;
}) => {
  if (isDefined(entryPoint) && isScheduleRoute(entryPoint)) {
    if (isDefined(postTime)) {
      return entryPoint;
    } else {
      return 'schedule.drafts';
    }
  } else {
    return defaultRoute;
  }
};

export const isScheduleRoute = (route: RoutableStateName) =>
  route.startsWith('schedule');

export const getIsPostInPostDetailsModal = (
  currentPostInModal: IScheduledPost | IDraftPost | undefined,
  post: IScheduledPost | IDraftPost,
) =>
  currentPostInModal?.id === post.id &&
  isDraftPost(currentPostInModal) === isDraftPost(post);

export const getPostStatusForBadge = (item: IScheduledPost | IDraftPost) =>
  isScheduledPost(item) &&
  item.customStatus === UniversalPostableCustomStatus.PostHidden
    ? PostableStatus.Posted
    : isScheduledPost(item)
      ? item.status
      : undefined;

export const updateCalendarUrlParams = (
  startDate: DateString,
  endDate: DateString,
  view: UniversalCalendarView,
) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('startDate', startDate);
  searchParams.set('endDate', endDate);
  searchParams.set('view', view);

  const updatedUrl = `${window.location.protocol}//${window.location.host}${
    window.location.pathname
  }?${searchParams.toString()}`;
  window.history.replaceState({}, document.title, updatedUrl);
};

export const getUniversalCalendarPostType = (postType: UniversalPostType) => {
  switch (postType) {
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
      return 'REEL';
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.InstagramStoryGroupPost:
      return 'STORY';
    default:
      return undefined;
  }
};

export const getTikTokPostDetailsFailureStatusMessage = (
  failure: ITikTokPostAttemptFailure,
) => {
  if (
    failure.name === 'TikTokApiProxyTokenException' ||
    failure.publishingFailure?.status === 401
  ) {
    return `We ran into a problem with your TikTok account permissions. You may need to reconnect your account and try again.`;
  }

  return getGenericPostTimeErrorMessage(SocialNetwork.TikTok);
};

export const getFacebookPostDetailsFailureStatusMessage = (
  failure: IFacebookPostAttemptFailure,
) => {
  switch (failure.error) {
    case 'aspect_ratio':
      return `The aspect ratio of this image must be between 4:5 and 1.91:1. Try again with a resized image.`;
    case 'daily_publishing_limit':
      return `Publishing failed. You have reached Facebook’s 24-hour posting limit of 25 posts.`;
    case 'feature_block':
      return `Publishing failed. You have reached Facebook’s short-term action limit. You will be unable to automatically post to Facebook for 12-24 hours.`;
    case 'image_size':
      return `Image too large to automatically post. Reduce the file size to less than ${FACEBOOK_MAX_IMAGE_FILE_SIZE_MB} MB and try again.`;
    case 'intermittent_upload_failure':
      return `Due to a Facebook issue, your post failed to publish at the scheduled time. Facebook’s support team is investigating. Try scheduling this post again.`;
    case 'token':
      return `We no longer have the necessary permissions for your Facebook account. Reconnect your account and try again.`;
    case 'unknown_graph_api':
    case 'unclassified':
    default:
      return getGenericPostTimeErrorMessage(SocialNetwork.Facebook);
  }
};

// Twitter error docs https://developer.x.com/en/support/x-api/error-troubleshooting
export const getTwitterPostDetailsFailureStatusMessage = (
  error: ITwitterPosterException,
) => {
  const errorCode = error.twitterApiError?.code;
  const errorMessage = error.twitterApiError?.message;

  if (error.type === 'MissingTwitterUserTokenException') {
    return `Something went wrong with your X account connection. Reconnect your account and try again.`;
  }

  const twitterApiMessage = errorMessage
    ? ` X’s API gave the following message: “${errorMessage}”`
    : '';

  if (errorCode === 401) {
    return `X could not authenticate your account.${twitterApiMessage} Reconnect your account and try again.`;
  }

  if (errorCode === 403) {
    if (errorMessage?.includes('suspended')) {
      return `According to X’s API, your X account is suspended.${twitterApiMessage}`;
    }
    if (errorMessage?.includes('locked')) {
      return `According to X’s API, your account is currently locked.${twitterApiMessage}`;
    }
  }

  if (errorMessage) {
    return `Posting failed.${twitterApiMessage}`;
  }

  return getGenericPostTimeErrorMessage(SocialNetwork.Twitter);
};
